import React, { useState } from 'react';

import classNames from 'classnames';

import { H3, P } from 'common/ui/Text';
import Field from 'common/uiV3/Field';
import Input from 'common/uiV3/Input';
import styles from 'css-module/components/subdomain/admin/AdminQueue/GettingStarted/_AdminAutopilotInfoBox.module.scss';

type ConfigurablePoint = {
  type: 'configurable';
  beforeText: string;
  afterText: string;
  placeholder: string;
};

type StaticPoint = {
  type: 'static';
  text: string;
};

type InfoPoint = ConfigurablePoint | StaticPoint;

type Props = {
  points: InfoPoint[];
  className?: string;
  onDayLimitChange?: (dayLimit: number) => void;
  dayLimit?: number;
  dayLimitDisabled?: boolean;
};

const AdminAutopilotInfoBox = ({
  points,
  className,
  onDayLimitChange,
  dayLimit,
  dayLimitDisabled = false,
}: Props) => {
  const [localDayLimit, setLocalDayLimit] = useState(dayLimit ?? 30);

  const handleDayLimitChange = (value: string) => {
    const newDayLimit = Number.parseInt(value) || 0;
    setLocalDayLimit(newDayLimit);
    onDayLimitChange?.(newDayLimit);
  };

  // Render info points with inline text input for day limit
  const renderInfoPoints = () => {
    return points.map((point, index) => {
      if (point.type === 'configurable') {
        return (
          <li className={styles.configurableItem} key={`info_box_${index}`}>
            <P className="text-base block">{point.beforeText}</P>
            <Field className="inline-flex w-auto min-w-0 align-baseline my-1">
              <Field.Content>
                <Input
                  aria-invalid={localDayLimit > 180}
                  autoFocus
                  disabled={dayLimitDisabled}
                  id="dayLimit"
                  onChange={(value: string) => handleDayLimitChange(value)}
                  placeholder={point.placeholder}
                  value={localDayLimit}
                />
              </Field.Content>
            </Field>
            <P className="text-base inline">{point.afterText}</P>
          </li>
        );
      }

      // Static points render normally
      return (
        <li className={styles.item} key={`info_box_${index}`}>
          <P>{point.text}</P>
        </li>
      );
    });
  };

  return (
    <div className={classNames(styles.container, className)}>
      <H3 variant="bodyMd" fontWeight="medium" className={styles.heading}>
        What happens when you connect:
      </H3>
      <ul className={styles.list}>{renderInfoPoints()}</ul>
      <P className={styles.learnMore}>
        Additional questions?{' '}
        <a
          href="https://help.canny.io/en/articles/8202451-autopilot#h_2db4f3ed39"
          rel="noopener"
          target="_blank">
          Check out the FAQ
        </a>
      </P>
    </div>
  );
};

export default AdminAutopilotInfoBox;
